import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AiComplianceAnalysisCard = ({ analysis, onRunAnalysis, loading }) => {
  const [activeSection, setActiveSection] = useState('violations');

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'text-error',
      high: 'text-warning',
      medium: 'text-primary',
      low: 'text-muted-foreground'
    };
    return colors?.[severity] || 'text-muted-foreground';
  };

  const getSeverityBg = (severity) => {
    const backgrounds = {
      critical: 'bg-error/10',
      high: 'bg-warning/10',
      medium: 'bg-primary/10',
      low: 'bg-muted'
    };
    return backgrounds?.[severity] || 'bg-muted';
  };

  const getRiskLevelColor = (level) => {
    const colors = {
      critical: 'text-error',
      high: 'text-warning',
      medium: 'text-primary',
      low: 'text-success'
    };
    return colors?.[level?.toLowerCase()] || 'text-muted-foreground';
  };

  const getRiskLevelBg = (level) => {
    const backgrounds = {
      critical: 'bg-error/10',
      high: 'bg-warning/10',
      medium: 'bg-primary/10',
      low: 'bg-success/10'
    };
    return backgrounds?.[level?.toLowerCase()] || 'bg-muted';
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'K0.00';
    return `K${amount?.toFixed(2)?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  };

  const sections = [
    { id: 'violations', label: 'Violations', icon: 'AlertTriangle' },
    { id: 'corrections', label: 'Payroll Corrections', icon: 'DollarSign' },
    { id: 'risk', label: 'DLIR Risk Assessment', icon: 'Shield' }
  ];

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="p-4 md:p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon name="Sparkles" size={20} color="var(--color-primary)" />
            <h3 className="font-heading font-semibold text-base md:text-lg text-foreground">
              AI-Powered Compliance Analysis
            </h3>
          </div>
          <Button
            variant="default"
            size="sm"
            onClick={onRunAnalysis}
            disabled={loading}
            iconName={loading ? 'Loader2' : 'Play'}
            iconPosition="left"
          >
            {loading ? 'Analyzing...' : 'Run Analysis'}
          </Button>
        </div>
        <p className="caption text-xs md:text-sm text-muted-foreground mt-1">
          AI-powered detection of PNG Employment Act violations, payroll corrections, and DLIR risk assessment
        </p>
      </div>

      {loading && (
        <div className="p-12 text-center">
          <Icon name="Loader2" size={48} color="var(--color-primary)" className="mx-auto mb-3 animate-spin" />
          <p className="caption text-sm text-muted-foreground">Analyzing compliance data...</p>
        </div>
      )}

      {!loading && !analysis && (
        <div className="p-12 text-center">
          <Icon name="Sparkles" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3 opacity-50" />
          <p className="caption text-sm text-muted-foreground mb-4">No analysis available. Click "Run Analysis" to start.</p>
        </div>
      )}

      {!loading && analysis && (
        <>
          <div className="p-4 border-b border-border">
            <div className="flex items-center gap-2 overflow-x-auto pb-2">
              {sections?.map(section => (
                <button
                  key={section?.id}
                  onClick={() => setActiveSection(section?.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-caption text-sm transition-smooth whitespace-nowrap ${
                    activeSection === section?.id
                      ? 'bg-primary text-white' :'bg-muted text-foreground hover:bg-muted/80'
                  }`}
                >
                  <Icon name={section?.icon} size={16} />
                  {section?.label}
                </button>
              ))}
            </div>
          </div>

          <div className="p-4 md:p-6">
            {activeSection === 'violations' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="p-4 bg-muted rounded-lg">
                    <p className="caption text-xs text-muted-foreground mb-1">Total Violations</p>
                    <p className="font-heading font-bold text-2xl text-foreground">
                      {analysis?.violations?.totalViolations || 0}
                    </p>
                  </div>
                  <div className="p-4 bg-error/10 rounded-lg">
                    <p className="caption text-xs text-muted-foreground mb-1">Critical</p>
                    <p className="font-heading font-bold text-2xl text-error">
                      {analysis?.violations?.criticalViolations || 0}
                    </p>
                  </div>
                  <div className="p-4 bg-primary/10 rounded-lg">
                    <p className="caption text-xs text-muted-foreground mb-1">Risk Level</p>
                    <p className={`font-heading font-bold text-lg capitalize ${getRiskLevelColor(analysis?.violations?.overallRiskLevel)}`}>
                      {analysis?.violations?.overallRiskLevel || 'N/A'}
                    </p>
                  </div>
                  <div className="p-4 bg-muted rounded-lg">
                    <p className="caption text-xs text-muted-foreground mb-1">Analyzed</p>
                    <p className="caption text-xs text-foreground">
                      {analysis?.analysisTimestamp ? new Date(analysis?.analysisTimestamp)?.toLocaleString('en-PG') : 'N/A'}
                    </p>
                  </div>
                </div>

                <div className="space-y-3">
                  {analysis?.violations?.violations?.map((violation, index) => (
                    <div key={index} className="border border-border rounded-lg p-4 hover:shadow-sm transition-smooth">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2">
                          <Icon name="AlertTriangle" size={18} color={getSeverityColor(violation?.severity)?.replace('text-', 'var(--color-)')} />
                          <h4 className="font-heading font-semibold text-sm text-foreground capitalize">
                            {violation?.violationType?.replace(/_/g, ' ')}
                          </h4>
                        </div>
                        <span className={`px-2 py-1 ${getSeverityBg(violation?.severity)} ${getSeverityColor(violation?.severity)} text-xs font-caption rounded-full capitalize`}>
                          {violation?.severity}
                        </span>
                      </div>
                      <p className="caption text-sm text-muted-foreground mb-2">{violation?.description}</p>
                      {violation?.regulation && (
                        <p className="caption text-xs text-primary mb-2">
                          <strong>Regulation:</strong> {violation?.regulation}
                        </p>
                      )}
                      {violation?.affectedCount > 0 && (
                        <div className="flex items-center gap-2 mb-2">
                          <Icon name="Users" size={14} color="var(--color-muted-foreground)" />
                          <p className="caption text-xs text-muted-foreground">
                            Affected: <span className="font-medium text-foreground">{violation?.affectedCount} employees</span>
                          </p>
                        </div>
                      )}
                      <div className="p-3 bg-primary/5 border-l-4 border-primary rounded">
                        <p className="caption text-xs font-medium text-foreground mb-1">Corrective Action</p>
                        <p className="caption text-xs text-muted-foreground">{violation?.correctiveAction}</p>
                      </div>
                      {violation?.estimatedCost > 0 && (
                        <div className="mt-2 flex items-center gap-2">
                          <Icon name="DollarSign" size={14} color="var(--color-warning)" />
                          <p className="caption text-xs text-warning">
                            Estimated Cost: <strong>{formatCurrency(violation?.estimatedCost)}</strong>
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {(!analysis?.violations?.violations || analysis?.violations?.violations?.length === 0) && (
                  <div className="py-8 text-center">
                    <Icon name="CheckCircle" size={48} color="var(--color-success)" className="mx-auto mb-3" />
                    <p className="caption text-sm text-success font-medium">No violations detected</p>
                  </div>
                )}
              </div>
            )}

            {activeSection === 'corrections' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                  <div className="p-4 bg-muted rounded-lg">
                    <p className="caption text-xs text-muted-foreground mb-1">Total Corrections</p>
                    <p className="font-heading font-bold text-2xl text-foreground">
                      {analysis?.corrections?.corrections?.length || 0}
                    </p>
                  </div>
                  <div className="p-4 bg-warning/10 rounded-lg">
                    <p className="caption text-xs text-muted-foreground mb-1">Total Backpay</p>
                    <p className="font-heading font-bold text-xl text-warning">
                      {formatCurrency(analysis?.corrections?.totalBackpayRequired)}
                    </p>
                  </div>
                  <div className="p-4 bg-primary/10 rounded-lg">
                    <p className="caption text-xs text-muted-foreground mb-1">Affected Employees</p>
                    <p className="font-heading font-bold text-2xl text-primary">
                      {analysis?.corrections?.affectedEmployeeCount || 0}
                    </p>
                  </div>
                </div>

                <div className="space-y-3">
                  {analysis?.corrections?.corrections?.map((correction, index) => (
                    <div key={index} className="border border-border rounded-lg p-4 hover:shadow-sm transition-smooth">
                      <div className="flex items-start justify-between gap-2 mb-3">
                        <div>
                          <h4 className="font-heading font-semibold text-sm text-foreground">
                            {correction?.employeeName || correction?.employeeId}
                          </h4>
                          <p className="caption text-xs text-muted-foreground capitalize">
                            {correction?.correctionType?.replace(/_/g, ' ')}
                          </p>
                        </div>
                        <span className={`px-2 py-1 ${
                          correction?.priority === 'immediate' ? 'bg-error/10 text-error' :
                          correction?.priority === 'next_pay_period'? 'bg-warning/10 text-warning' : 'bg-primary/10 text-primary'
                        } text-xs font-caption rounded-full capitalize`}>
                          {correction?.priority?.replace(/_/g, ' ')}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mb-3">
                        <div>
                          <p className="caption text-xs text-muted-foreground mb-1">Current Value</p>
                          <p className="font-heading font-medium text-sm text-foreground">
                            {formatCurrency(correction?.currentValue)}
                          </p>
                        </div>
                        <div>
                          <p className="caption text-xs text-muted-foreground mb-1">Corrected Value</p>
                          <p className="font-heading font-medium text-sm text-success">
                            {formatCurrency(correction?.correctedValue)}
                          </p>
                        </div>
                      </div>

                      {correction?.backpayAmount > 0 && (
                        <div className="p-2 bg-warning/10 rounded mb-3">
                          <p className="caption text-xs text-warning">
                            <strong>Backpay Required:</strong> {formatCurrency(correction?.backpayAmount)}
                          </p>
                        </div>
                      )}

                      <div className="p-3 bg-muted rounded mb-2">
                        <p className="caption text-xs font-medium text-foreground mb-1">Calculation Method</p>
                        <p className="caption text-xs text-muted-foreground">{correction?.calculationMethod}</p>
                      </div>

                      {correction?.notes && (
                        <p className="caption text-xs text-muted-foreground italic">
                          <strong>Notes:</strong> {correction?.notes}
                        </p>
                      )}
                    </div>
                  ))}
                </div>

                {analysis?.corrections?.implementationSteps?.length > 0 && (
                  <div className="mt-6 p-4 bg-primary/5 border border-primary rounded-lg">
                    <h4 className="font-heading font-semibold text-sm text-foreground mb-3">Implementation Steps</h4>
                    <ol className="space-y-2">
                      {analysis?.corrections?.implementationSteps?.map((step, index) => (
                        <li key={index} className="caption text-xs text-muted-foreground flex items-start gap-2">
                          <span className="font-bold text-primary">{index + 1}.</span>
                          <span>{step}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}

                {(!analysis?.corrections?.corrections || analysis?.corrections?.corrections?.length === 0) && (
                  <div className="py-8 text-center">
                    <Icon name="CheckCircle" size={48} color="var(--color-success)" className="mx-auto mb-3" />
                    <p className="caption text-sm text-success font-medium">No payroll corrections needed</p>
                  </div>
                )}
              </div>
            )}

            {activeSection === 'risk' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                  <div className={`p-4 ${getRiskLevelBg(analysis?.riskAssessment?.overallRiskLevel)} rounded-lg`}>
                    <p className="caption text-xs text-muted-foreground mb-1">Overall Risk</p>
                    <p className={`font-heading font-bold text-2xl capitalize ${getRiskLevelColor(analysis?.riskAssessment?.overallRiskLevel)}`}>
                      {analysis?.riskAssessment?.overallRiskLevel || 'N/A'}
                    </p>
                  </div>
                  <div className="p-4 bg-muted rounded-lg">
                    <p className="caption text-xs text-muted-foreground mb-1">Risk Score</p>
                    <p className="font-heading font-bold text-2xl text-foreground">
                      {analysis?.riskAssessment?.riskScore || 0}/100
                    </p>
                  </div>
                  <div className="p-4 bg-success/10 rounded-lg">
                    <p className="caption text-xs text-muted-foreground mb-1">Compliance Confidence</p>
                    <p className="font-heading font-bold text-2xl text-success">
                      {analysis?.riskAssessment?.complianceConfidenceScore || 0}%
                    </p>
                  </div>
                </div>

                {analysis?.riskAssessment?.summary && (
                  <div className="p-4 bg-primary/5 border-l-4 border-primary rounded-lg mb-6">
                    <h4 className="font-heading font-semibold text-sm text-foreground mb-2">Executive Summary</h4>
                    <p className="caption text-sm text-muted-foreground">{analysis?.riskAssessment?.summary}</p>
                  </div>
                )}

                {analysis?.riskAssessment?.keyRiskFactors?.length > 0 && (
                  <div className="mb-6">
                    <h4 className="font-heading font-semibold text-sm text-foreground mb-3">Key Risk Factors</h4>
                    <div className="space-y-2">
                      {analysis?.riskAssessment?.keyRiskFactors?.map((risk, index) => (
                        <div key={index} className="border border-border rounded-lg p-3">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <p className="font-heading font-medium text-sm text-foreground">{risk?.factor}</p>
                            <span className={`px-2 py-1 ${getSeverityBg(risk?.severity)} ${getSeverityColor(risk?.severity)} text-xs font-caption rounded-full capitalize`}>
                              {risk?.severity}
                            </span>
                          </div>
                          <p className="caption text-xs text-muted-foreground">{risk?.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {analysis?.riskAssessment?.recommendedActions?.length > 0 && (
                  <div className="mb-6">
                    <h4 className="font-heading font-semibold text-sm text-foreground mb-3">Recommended Actions</h4>
                    <div className="space-y-2">
                      {analysis?.riskAssessment?.recommendedActions?.map((action, index) => (
                        <div key={index} className="border border-border rounded-lg p-3">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <p className="caption text-sm text-foreground">{action?.action}</p>
                            <span className={`px-2 py-1 ${
                              action?.priority === 'high' ? 'bg-error/10 text-error' :
                              action?.priority === 'medium'? 'bg-warning/10 text-warning' : 'bg-primary/10 text-primary'
                            } text-xs font-caption rounded-full capitalize`}>
                              {action?.priority}
                            </span>
                          </div>
                          {action?.deadline && (
                            <div className="flex items-center gap-1 mt-2">
                              <Icon name="Calendar" size={12} color="var(--color-muted-foreground)" />
                              <p className="caption text-xs text-muted-foreground">Deadline: {action?.deadline}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {analysis?.riskAssessment?.estimatedFinancialExposure > 0 && (
                  <div className="p-4 bg-warning/10 border border-warning rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="AlertTriangle" size={18} color="var(--color-warning)" />
                      <h4 className="font-heading font-semibold text-sm text-warning">Financial Exposure</h4>
                    </div>
                    <p className="font-heading font-bold text-2xl text-warning">
                      {formatCurrency(analysis?.riskAssessment?.estimatedFinancialExposure)}
                    </p>
                    <p className="caption text-xs text-muted-foreground mt-1">
                      Potential fines, penalties, and backpay if violations are not addressed
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AiComplianceAnalysisCard;